import { useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  Button, Input, Table, message, Select, Tag, Space, Tooltip, Spin, Alert,
} from "antd";
import {
  SearchOutlined, SaveOutlined, ReloadOutlined, TranslationOutlined,
  ClearOutlined, FileTextOutlined,
} from "@ant-design/icons";
import { APP_LANGUAGES, fixedStart } from "../../../i18";
import { ListPage, T, cardStyle } from "../../../shared/ui/PageShell";
import useTablePagination from "../../../shared/ui/useTablePagination";
import {
  loadTranslationOverrides,
  saveTranslationOverrides,
  getTranslationMeta,
} from "../../../shared/lib/translationOverrides";

async function fetchLocaleFile(locale) {
  const res = await fetch(`/locales/${locale}/translation.json?v=${Date.now()}`);
  if (!res.ok) return {};
  const data = await res.json();
  return data && typeof data === "object" ? data : {};
}

async function fetchUsageMap() {
  try {
    const res = await fetch(`/locales/usage.json?v=${Date.now()}`);
    if (!res.ok) return {};
    const data = await res.json();
    return data && typeof data === "object" ? data : {};
  } catch {
    return {};
  }
}

const TranslationSettings = () => {
  const { t, i18n } = useTranslation();
  const { pagination } = useTablePagination();
  const [locale, setLocale] = useState("en");
  const [baseEn, setBaseEn] = useState({});
  const [fileBag, setFileBag] = useState({});
  const [usage, setUsage] = useState({});
  const [drafts, setDrafts] = useState({});
  const [search, setSearch] = useState("");
  const [filter, setFilter] = useState("all"); // all | dirty | unused | used
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [writable, setWritable] = useState(true);
  const [pathHint, setPathHint] = useState("");

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [enBag, locBag, , useMap] = await Promise.all([
        fetchLocaleFile("en"),
        fetchLocaleFile(locale),
        // Migrates any legacy DB overlays into the JSON files on the server.
        loadTranslationOverrides({ force: true }),
        fetchUsageMap(),
      ]);
      setBaseEn(enBag);
      setFileBag(locBag);
      setUsage(useMap);
      setDrafts({});
      const meta = getTranslationMeta();
      if (meta && typeof meta.writable === "boolean") {
        setWritable(meta.writable);
      }
      if (meta) {
        const parts = [
          meta.hint,
          meta.locales_path
            ? t("settings.translations.path", { defaultValue: "Path: {{path}}", path: meta.locales_path })
            : "",
          meta.api_base_path
            ? t("settings.translations.apiPath", { defaultValue: "API: {{path}}", path: meta.api_base_path })
            : "",
          meta.exists === false
            ? t("settings.translations.folderMissing", { defaultValue: "Folder missing" })
            : "",
          meta.en_file_exists === false
            ? t("settings.translations.englishFileMissing", { defaultValue: "en/translation.json missing" })
            : "",
        ].filter(Boolean);
        setPathHint(parts.join(" — "));
      }
    } catch {
      message.error(t("settingsErrLoad", "Failed to load"));
    } finally {
      setLoading(false);
    }
  }, [locale, t]);

  useEffect(() => { load(); }, [load]);

  const rows = useMemo(() => {
    const keys = new Set([
      ...Object.keys(baseEn),
      ...Object.keys(fileBag),
      ...Object.keys(drafts),
    ]);
    const term = search.trim().toLowerCase();
    const list = [];
    keys.forEach((key) => {
      const fileVal = fileBag[key] ?? "";
      const draftVal = Object.prototype.hasOwnProperty.call(drafts, key) ? drafts[key] : undefined;
      const effective = draftVal !== undefined ? draftVal : fileVal;
      const places = usage[key] || [];
      const isUnused = places.length === 0;
      const dirty = draftVal !== undefined;

      if (filter === "dirty" && !dirty) return;
      if (filter === "unused" && !isUnused) return;
      if (filter === "used" && isUnused) return;

      if (term) {
        const hay = [
          key,
          String(baseEn[key] || ""),
          String(fileVal || ""),
          String(draftVal || ""),
          places.join(" "),
        ].join(" ").toLowerCase();
        if (!hay.includes(term)) return;
      }

      list.push({
        key,
        en: baseEn[key] ?? "",
        file: fileVal,
        draft: draftVal,
        effective,
        places,
        dirty,
      });
    });
    list.sort((a, b) => a.key.localeCompare(b.key));
    return list;
  }, [baseEn, fileBag, drafts, usage, search, filter]);

  const dirtyCount = Object.keys(drafts).length;
  const canSave = dirtyCount > 0 && writable;
  const saveDisabledReason = !writable
    ? t(
        "settingsTranslationsNotWritable",
        "Translation folder is not writable."
      )
    : !dirtyCount
      ? t(
          "settingsTranslationsEditFirst",
          "Edit at least one translation before saving."
        )
      : "";

  const setDraft = (key, value) => {
    setDrafts((prev) => {
      const next = { ...prev };
      const baseline = fileBag[key] ?? "";
      if (String(value) === String(baseline)) {
        delete next[key];
      } else {
        next[key] = value;
      }
      return next;
    });
  };

  /** Clear = remove key from this locale file (falls back to English at runtime). */
  const clearKey = (key) => {
    setDrafts((prev) => ({ ...prev, [key]: null }));
  };

  const handleSave = async () => {
    if (!dirtyCount) return;
    setSaving(true);
    try {
      await saveTranslationOverrides(locale, { ...drafts });
      setDrafts({});
      message.success(t("settingsTranslationsSavedFile", "Saved to translation file"));
      const locBag = await fetchLocaleFile(locale);
      setFileBag(locBag);
      if (locale === "en") setBaseEn(locBag);
    } catch (e) {
      message.error(e?.response?.data?.message || t("settingsSaveError", "Save failed"));
    } finally {
      setSaving(false);
    }
  };

  const columns = [
    {
      title: <b>{t("settingsTranslationKey", "Key")}</b>,
      dataIndex: "key",
      width: 220,
      fixed: fixedStart(i18n.language),
      render: (v) => (
        <code style={{ fontSize: 12, color: T.text, wordBreak: "break-all" }}>{v}</code>
      ),
    },
    {
      title: <b>{t("settingsTranslationUsedIn", "Used in")}</b>,
      dataIndex: "places",
      width: 260,
      render: (places) => {
        if (!places?.length) {
          return <Tag style={{ borderRadius: 6 }}>{t("settingsTranslationUnused", "Not found in code")}</Tag>;
        }
        const shown = places.slice(0, 3);
        const extra = places.length - shown.length;
        return (
          <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
            {shown.map((p) => (
              <Tooltip key={p} title={p}>
                <span style={{ fontSize: 11, color: T.sub, display: "flex", alignItems: "center", gap: 4 }}>
                  <FileTextOutlined style={{ fontSize: 10 }} />
                  {p.replace(/^src\//, "")}
                </span>
              </Tooltip>
            ))}
            {extra > 0 && (
              <Tooltip title={places.slice(3).join("\n")}>
                <Tag style={{ borderRadius: 6, width: "fit-content" }}>+{extra}</Tag>
              </Tooltip>
            )}
          </div>
        );
      },
    },
    {
      title: <b>EN</b>,
      dataIndex: "en",
      width: 200,
      render: (v) => <span style={{ fontSize: 12, color: T.muted }}>{v || "—"}</span>,
    },
    {
      title: <b>{t("settingsTranslationValue", "Value")} ({locale})</b>,
      key: "value",
      render: (_, row) => {
        const clearing = row.draft === null;
        const value = clearing
          ? ""
          : (row.draft !== undefined ? row.draft : row.file);
        return (
          <div style={{ display: "flex", gap: 6, alignItems: "center" }}>
            <Input.TextArea
              autoSize={{ minRows: 1, maxRows: 4 }}
              value={value ?? ""}
              placeholder={clearing
                ? t("settingsTranslationWillRemoveKey", "Will remove from this language file")
                : ""}
              onChange={(e) => setDraft(row.key, e.target.value)}
              style={{
                borderRadius: 8,
                borderColor: row.dirty ? "#2563eb" : undefined,
                background: clearing ? "#fff7ed" : undefined,
              }}
            />
            {(row.file || row.dirty) && locale !== "en" && (
              <Tooltip title={t("settingsTranslationRemoveKey", "Remove from this language file")}>
                <Button
                  size="small"
                  icon={<ClearOutlined />}
                  onClick={() => clearKey(row.key)}
                  style={{ borderRadius: 7, flexShrink: 0 }}
                />
              </Tooltip>
            )}
          </div>
        );
      },
    },
    {
      title: "",
      key: "flags",
      width: 100,
      render: (_, row) => (
        row.dirty
          ? <Tag color="blue" style={{ borderRadius: 6 }}>{t("edited", "Edited")}</Tag>
          : null
      ),
    },
  ];

  return (
    <ListPage
      breadcrumb={[t("sidebarSectionSettings", "Settings"), t("settingsTranslationsTitle", "Languages")]}
      title={t("settingsTranslationsTitle", "Languages & translations")}
      subtitle={t(
        "settingsTranslationsHintFiles",
        "Edits are saved into the real translation files (public/locales). All users see them after refresh."
      )}
      action={
        <Space wrap>
          <Button icon={<ReloadOutlined />} onClick={load} loading={loading} style={{ borderRadius: 9 }}>
            {t("currencyTotalsRefresh", "Refresh")}
          </Button>
          <Tooltip title={!canSave ? saveDisabledReason : undefined}>
            <Button
              type="primary"
              icon={<SaveOutlined />}
              disabled={!canSave}
              loading={saving}
              onClick={handleSave}
              style={{ borderRadius: 9, fontWeight: 600 }}
            >
              {t("settingsSave", "Save")}{dirtyCount ? ` (${dirtyCount})` : ""}
            </Button>
          </Tooltip>
        </Space>
      }
    >
      {!writable && (
        <Alert
          type="warning"
          showIcon
          style={{ marginBottom: 14, borderRadius: 10 }}
          message={t(
            "settingsTranslationsNotWritable",
            "Translation folder is not writable."
          )}
          description={
            <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
              <span>
                {pathHint ||
                  t("settings.translations.pathConfigurationHint", {
                    defaultValue: "In api/.env set: FRONTEND_LOCALES_PATH=../locales (when locales sits next to the api folder)",
                  })}
              </span>
              <span style={{ color: T.sub }}>
                {t(
                  "settingsTranslationsWritableFix",
                  "On the server: point FRONTEND_LOCALES_PATH at the folder that contains en/ku/ar/tr translation.json, then chmod that folder (and those files) to 775 so PHP can write. Save is disabled until the API reports writable=true."
                )}
              </span>
            </div>
          }
        />
      )}

      <div style={{ ...cardStyle, padding: 16, marginBottom: 14 }}>
        <div style={{ display: "flex", flexWrap: "wrap", gap: 10, alignItems: "center" }}>
          <Select
            value={locale}
            onChange={setLocale}
            style={{ width: 180, borderRadius: 9 }}
            options={APP_LANGUAGES.map((l) => ({ value: l.value, label: l.label }))}
          />
          <Input
            allowClear
            prefix={<SearchOutlined style={{ color: T.muted }} />}
            placeholder={t("settingsTranslationsSearch", "Search key, text, or file path…")}
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            style={{ width: 340, maxWidth: "100%", borderRadius: 9, height: 38 }}
          />
          <Select
            value={filter}
            onChange={setFilter}
            style={{ width: 170 }}
            options={[
              { value: "all", label: t("all", "All") },
              { value: "used", label: t("settingsTranslationFilterUsed", "Used in code") },
              { value: "unused", label: t("settingsTranslationFilterUnused", "Unused keys") },
              { value: "dirty", label: t("settingsTranslationFilterEdited", "Edited only") },
            ]}
          />
          <Tag icon={<TranslationOutlined />} style={{ borderRadius: 8, margin: 0 }}>
            {rows.length} {t("settingsTranslationKeys", "keys")}
          </Tag>
          <Tag style={{ borderRadius: 8, margin: 0 }}>
            locales/{locale}/translation.json
          </Tag>
        </div>
      </div>

      <Spin spinning={loading}>
        <Table
          rowKey="key"
          columns={columns}
          dataSource={rows}
          pagination={{ ...pagination, pageSizeOptions: [20, 40, 80, 200] }}
          scroll={{ x: 1100 }}
          size="small"
          rowClassName={(r) => (r.dirty ? "tr-settings-dirty" : "")}
        />
      </Spin>
    </ListPage>
  );
};

export default TranslationSettings;
